import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
// 导入统一内容渲染器和工具
import UnifiedContentRenderer from './components/core/UnifiedContentRenderer';
import { processContent } from './components/core/ContentUtils';
// 不再需要独立的样式，因为它现在由父组件的ChatInterface.module.css统一控制

// 新增辅助函数：用于检测文本是否为Markdown表格
const isMarkdownTable = (text) => {
  if (!text || typeof text !== 'string') {
    return false;
  }
  const lines = text.trim().split('\n');
  // Markdown表格至少需要3行（表头、分隔线、内容）
  if (lines.length < 2) {
    return false;
  }
  // 检查第二行是否为分隔线
  const separatorLine = lines[1].trim();
  return /^\s*\|?.*[-|:]{3,}.*\|?\s*$/.test(separatorLine);
};

const ExpertMessage = React.memo(
  function ExpertMessage({ 
    content, 
    messageIndex, 
    adoptedOpinions, 
    onOpinionAdoption, 
    isError, 
    isProcessing,
    styles, // 从父组件接收统一的样式对象
    onA2UIAction = null, // 🆕 A2UI动作回调
    messageId = null,    // 🆕 消息ID作为SurfaceID
    isStreaming = false,
  }) {
    const systemPromptLines = [
      `(1)在专家咨询团队中选择你想要咨询的专家，仔细阅读专家提供的意见，勾选您认为有价值的建议。点击"发送"键以更新文档。`,
      `(2)如有个人想法或建议，请直接输入。`,
      `(3)如需更多专业建议，可继续咨询其他专家，以获取更全面的意见。`,
      `(4)如无需采纳任何新意见，请回复命令 @yes 以正式提交上一轮的结果，并进入项目的下一阶段。`
    ];

    const isSystemPromptLine = (line) => {
      // 检查是否是动态的第一行提示
      const dynamicPromptRegex = /^以上是.*智能体对上一轮输出的建设性反馈[.。]您可以：$/;
      if (line.trim().match(dynamicPromptRegex)) {
        return true;
      }
      // 检查是否是固定的系统提示行
      return systemPromptLines.some(promptLine => line.trim() === promptLine.trim());
    };

    // 处理表格内容 - 这段逻辑可能不再需要，因为 UnifiedContentRenderer 会处理
    // const processTablesInContent = (content) => { ... };

    // 将内容分割成行，处理每一行
    const lines = content?.split('\n') || [];
    
    // 建立意见行到其在 opinions 数组中索引的映射
    const opinionLineIndices = {};
    let opinionCounter = 0;
    lines.forEach((line, lineIndex) => {
      if (line.trim().startsWith('- [') || line.trim().startsWith('* [')) {
        opinionLineIndices[lineIndex] = opinionCounter++;
      }
    });

    // 渲染内容函数
    const renderExpertContent = () => {
      if (isError) {
        return <div className={styles.errorState || 'error-state'}>加载出错，请刷新重试。</div>;
      }

      if (isProcessing) {
        return <div className={styles.processingState || 'processing-state'}>专家团队正在为您分析数据...</div>;
      }
      
      // 用于跟踪已被处理（例如作为Mermaid块一部分）的行索引
      const processedIndices = new Set();
      const elements = []; // 存储要渲染的JSX元素

      for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        // 如果当前行已被处理，跳过
        if (processedIndices.has(lineIndex)) {
          continue;
        }
        
        const line = lines[lineIndex];
        const trimmedLine = line.trim();

        // 1. 检测代码块（包括 Mermaid 和其他语言，但排除 markdown 表格）
        if (trimmedLine.startsWith('```') && !trimmedLine.startsWith('```markdown')) {
          let endIndex = -1;
          // 向前查找结束标记
          for (let j = lineIndex + 1; j < lines.length; j++) {
            if (lines[j].trim() === '```') {
              endIndex = j;
              break;
            }
          }

          if (endIndex !== -1) {
            // 找到了完整的代码块
            const codeBlockContent = lines.slice(lineIndex, endIndex + 1).join('\n');
            // 标记这个块的所有行为已处理
            for (let k = lineIndex; k <= endIndex; k++) {
              processedIndices.add(k);
            }
            
            // 检查是否是 Mermaid 块，如果是则特殊处理
            if (trimmedLine.startsWith('```mermaid')) {
              // 添加 Mermaid 块的渲染元素
              elements.push(
                <div key={`block-mermaid-${lineIndex}`} className={`${styles.contentBlock || ''} ${styles.expertContentBlock || ''}`}>
                  <UnifiedContentRenderer 
                    content={codeBlockContent} 
                    collapsible={false}
                    className={styles.unifiedContent || ''}
                    compactMode={true}
                    renderOptions={{ /* UCR 会自动处理 Mermaid */ }}
                    onA2UIAction={onA2UIAction}
                    messageId={messageId}
                    isStreaming={isStreaming}
                  />
                </div>
              );
            } else {
              // 处理其他语言的代码块
              elements.push(
                <div key={`block-code-${lineIndex}`} className={styles.codeBlockWrapper}>
                  <UnifiedContentRenderer 
                    content={codeBlockContent} 
                    collapsible={false}
                    className={styles.unifiedContent || ''}
                    compactMode={true}
                    showCodeBlockHeader={false}
                    renderOptions={{
                      customClass: styles.expertMarkdown || '',
                      tableClass: styles.expertTable || '',
                      codeClass: styles.expertCode || '',
                      nestingLevel: 0
                    }}
                    onA2UIAction={onA2UIAction}
                    messageId={messageId}
                  />
                </div>
              );
            }
            // 继续下一次外层循环
            continue; 
          }

          // 流式中的 Mermaid 即使暂时出现了代码围栏，也尚未保证语法完整。
          // 不尝试渲染，避免把正常的中间态显示为“图表渲染失败”。
          if (trimmedLine.startsWith('```mermaid') && isStreaming) {
            elements.push(
              <div key={`streaming-mermaid-${lineIndex}`} className={`${styles.contentBlock || ''} ${styles.expertContentBlock || ''}`}>
                <UnifiedContentRenderer
                  content={lines.slice(lineIndex).join('\n')}
                  collapsible={false}
                  className={styles.unifiedContent || ''}
                  compactMode={true}
                  isStreaming
                />
              </div>
            );
            break;
          }
          // 非流式且未闭合的围栏按普通文本降级显示。
        }

        // 2. 处理系统提示行
        const isPrompt = isSystemPromptLine(line);
        if (isPrompt) {
          processedIndices.add(lineIndex);
          elements.push(
            <div key={`prompt-${lineIndex}`} className={styles.systemPrompt || 'system-prompt'}>
              {line}
            </div>
          );
          continue;
        }

        // 3. 处理意见行
        const isOpinion = trimmedLine.startsWith('- [') || trimmedLine.startsWith('* [');
        if (isOpinion) {
          processedIndices.add(lineIndex);
          const opinionMatch = line.match(/^[*-] \[([ x])\] (.+)$/);
          const opinionContent = opinionMatch ? opinionMatch[2] : line;
          const opinionIndex = opinionLineIndices[lineIndex]; // 使用预先计算的索引
          const isAdopted = adoptedOpinions.includes(opinionIndex);
          
          elements.push(
            <div 
              key={`opinion-${lineIndex}`}
              className={`${styles.opinion || ''} ${isAdopted ? (styles.adoptedOpinion || '') : ''}`}
              onClick={() => {
                if (onOpinionAdoption && messageIndex !== -1) {
                  onOpinionAdoption(opinionIndex, !isAdopted);
                }
              }}
            >
              <div className={styles.opinionCheckbox || 'opinion-checkbox'}>
                <input type="checkbox" checked={isAdopted} readOnly />
              </div>
              <div className={styles.opinionContent || 'opinion-content'}>{opinionContent}</div>
            </div>
          );
          continue;
        }

        // 4. 处理空行：折叠连续空行，避免导师气泡出现两三行的大空白
        if (trimmedLine === '') {
          processedIndices.add(lineIndex);
          const hasPreviousContent = lines.slice(0, lineIndex).some(item => item.trim() !== '');
          const hasNextContent = lines.slice(lineIndex + 1).some(item => item.trim() !== '');
          const previousLineIsBlank = lineIndex > 0 && lines[lineIndex - 1].trim() === '';

          if (hasPreviousContent && hasNextContent && !previousLineIsBlank) {
            elements.push(
              <div
                key={`empty-${lineIndex}`}
                className={styles.emptyLine || 'empty-line'}
                aria-hidden="true"
              />
            );
          }
          continue;
        }

        // 5. 处理普通文本块 (合并连续的普通文本行)
        let textBlockLines = [];
        let currentTextIndex = lineIndex;
        // 合并直到遇到特殊行类型或文件末尾
        while (
          currentTextIndex < lines.length &&
          !processedIndices.has(currentTextIndex) &&
          !lines[currentTextIndex].trim().startsWith('```') && // 停止于任何 ``` 标记
          !isSystemPromptLine(lines[currentTextIndex]) &&
          !(lines[currentTextIndex].trim().startsWith('- [') || lines[currentTextIndex].trim().startsWith('* [')) &&
          lines[currentTextIndex].trim() !== ''
        ) {
          textBlockLines.push(lines[currentTextIndex]);
          processedIndices.add(currentTextIndex); // 标记为已处理
          currentTextIndex++;
        }

        // 如果找到了文本块，渲染它
        if (textBlockLines.length > 0) {
          const textBlockContent = textBlockLines.join('\n');
          
          // **核心修改**：在这里判断文本块是表格还是普通MD
          const isTable = isMarkdownTable(textBlockContent);

          elements.push(
            <div 
              key={`block-text-${lineIndex}`} 
              className={isTable ? styles.tableContainer : styles.markdownContainer}
            >
              <UnifiedContentRenderer 
                content={textBlockContent}
                collapsible={false}
                compactMode={true}
                renderOptions={{
                  // 我们不再需要通过renderOptions传递类名
                  // 因为外层容器已经应用了正确的样式
                }}
                onA2UIAction={onA2UIAction}
                messageId={messageId}
              />
            </div>
          );
          // 由于我们已经在循环中标记了 processedIndices，外层循环会自动跳过这些行
          // 将 lineIndex 设置到这个块的末尾，以便下一次迭代从正确的位置开始
          // 注意：因为外层循环会自动+1，所以这里设置为currentTextIndex - 1
          lineIndex = currentTextIndex - 1; 
        } else {
          // 如果当前行无法归类（理论上不应发生），则标记为已处理以防无限循环
          if (!processedIndices.has(lineIndex)) { // 再次检查以防万一
             processedIndices.add(lineIndex);
          }
        }
      } // End of for loop

      return (
        <div className={styles.expertContent || 'expert-content'}>
          {elements} 
        </div>
      );
    };

    // 创建自定义的专家消息样式
    const ExpertContentStyles = () => (
      <style jsx>{`
        .${styles.expertContent} {
          font-family: var(--font-family, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif);
          line-height: 1.42;
        }
        
        .${styles.expertContentBlock} {
          margin: 4px 0;
          padding: 0;
          background-color: var(--expert-block-bg, transparent);
          border-radius: 4px;
        }
        
        .${styles.expertLine} {
          margin: 4px 0;
          padding: 0;
        }
        
        .${styles.expertContentInline} :global(p) {
          margin: 0 !important;
          padding: 0 !important;
          display: inline;
        }
        
        .${styles.expertContentBlock} :global(.code-block-wrapper) {
          margin: 10px 0;
          border-radius: 6px;
          overflow: hidden;
        }
        
        .${styles.expertContentBlock} :global(.table-responsive) {
          margin: 12px 0;
          border-radius: 4px;
          overflow-x: auto;
        }
        
        .${styles.expertContentBlock} :global(table) {
          border-collapse: collapse;
          width: 100%;
          border: 1px solid var(--table-border-color, #444);
        }
        
        .${styles.expertContentBlock} :global(th) {
          background-color: var(--th-bg-color, #333);
          color: var(--th-color, #eee);
          font-weight: 600;
          padding: 8px 12px;
          text-align: left;
          border: 1px solid var(--table-border-color, #444);
        }
        
        .${styles.expertContentBlock} :global(td) {
          padding: 6px 12px;
          border: 1px solid var(--table-border-color, #444);
        }
        
        .${styles.expertContentBlock} :global(code) {
          background-color: var(--code-bg, #2a2a2a);
          color: var(--code-color, #e5e5e5);
          padding: 0.2em 0.4em;
          border-radius: 3px;
          font-family: var(--font-mono, monospace);
          font-size: 0.9em;
        }
      `}</style>
    );
                
    return (
      <div className={`${styles.expertMessage || 'expert-message'} ${isError ? styles.errorContainer || 'error-container' : ''}`}>
        {renderExpertContent()}
        <ExpertContentStyles />
      </div>
    );
  }
);

export default ExpertMessage;